import React, { useState, useEffect, useMemo } from 'react';
import {
  Search, Filter, Mail, Calendar, Star, StarOff, MessageSquare,
  Download, Edit, ChevronLeft, ChevronRight, X, Clock, User,
  AlertCircle, TrendingUp, RefreshCw, Save, CheckCircle,
  XCircle, Bell, FileText, MoreVertical, Trash2, Send
} from 'lucide-react';
import axios from 'axios';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import demoRequestService from "../../service/demo-request.service";
import { useNavigate } from "react-router-dom";
import config from '../../environment/config';
import './AdminDemoRequests.css';
import SecondaryFooter from '../Common/SecondaryFooter';
import AdminLayout from '../Common/AdminLayout';

const API_BASE_URL = `${config.API_URL}/api/seo-user`;

// Toast Notification Component
const Toast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 3000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`adr-toast adr-toast-${type}`}>
      <div className="adr-toast-icon">
        {type === 'success' && <CheckCircle size={20} />}
        {type === 'error' && <XCircle size={20} />}
        {type === 'info' && <Bell size={20} />}
      </div>
      <span className="adr-toast-message">{message}</span>
      <button className="adr-toast-close" onClick={onClose}>
        <X size={16} />
      </button>
    </div>
  );
};

// Confirmation Modal Component
const ConfirmModal = ({ title, message, onConfirm, onCancel }) => (
  <div className="adr-modal-overlay" onClick={onCancel}>
    <div className="adr-modal" onClick={(e) => e.stopPropagation()}>
      <div className="adr-modal-header">
        <AlertCircle size={24} className="adr-modal-icon" />
        <h3>{title}</h3>
      </div>
      <div className="adr-modal-body">
        <p>{message}</p>
      </div>
      <div className="adr-modal-actions">
        <button className="adr-btn-modal adr-btn-cancel" onClick={onCancel}>
          Cancel
        </button>
        <button className="adr-btn-modal adr-btn-confirm" onClick={onConfirm}>
          Confirm
        </button>
      </div>
    </div>
  </div>
);

// Loading Skeleton Component
const TableSkeleton = () => (
  <div className="adr-skeleton-table">
    {[...Array(5)].map((_, i) => (
      <div key={i} className="adr-skeleton-row">
        <div className="adr-skeleton-cell adr-skeleton-animate" style={{ width: '80px' }} />
        <div className="adr-skeleton-cell adr-skeleton-animate" style={{ width: '150px' }} />
        <div className="adr-skeleton-cell adr-skeleton-animate" style={{ width: '200px' }} />
        <div className="adr-skeleton-cell adr-skeleton-animate" style={{ width: '120px' }} />
        <div className="adr-skeleton-cell adr-skeleton-animate" style={{ width: '100px' }} />
      </div>
    ))}
  </div>
);

// Empty State Component
const EmptyState = ({ message, icon: Icon }) => (
  <div className="adr-empty-state">
    <div className="adr-empty-icon">
      <Icon size={64} />
    </div>
    <h3>No Results Found</h3>
    <p>{message}</p>
  </div>
);

// Data transformation function
const transformApiDataToRequest = (apiData) => {
  let productName = apiData.productName || null;  // ✅ Get from API field
  let planName = apiData.planName || null;        // ✅ Get from API field
  let cleanMessage = apiData.message || '';

  // ✅ REMOVE this old logic that was parsing from message
  // if (cleanMessage.startsWith('Product:')) {
  //   const lines = cleanMessage.split('\n');
  //   productName = lines[0].replace('Product:', '').trim();
  //   cleanMessage = lines.slice(2).join('\n').trim();
  // }

  let parsedNotes = [];
  if (apiData.comment && apiData.comment.trim()) {
    const noteEntries = apiData.comment.split('\n\n').filter(n => n.trim());
    parsedNotes = noteEntries.map((noteText) => {
      const match = noteText.match(/\[(.*?)\]\s*(.*?):\s*(.*)/s);
      if (match) {
        return {
          text: match[3].trim(),
          user: match[2].trim(),
          timestamp: new Date(match[1]).toISOString()
        };
      }
      return {
        text: noteText.trim(),
        user: 'Admin',
        timestamp: apiData.createdDate || new Date().toISOString()
      };
    });
  }

  const backendStatus = apiData.status || 'New';
  const backendPriority = apiData.priority ||
    (apiData.type === 'Enterprise' ? 'High' : apiData.type === 'Organisation' ? 'Medium' : 'Low');

  return {
    id: `DR-${String(apiData.demoRequestId).padStart(5, '0')}`,
    name: apiData.name || 'Unknown',
    email: apiData.email || '',
    phone: apiData.phone ? `+91 ${apiData.phone}` : '',
    company: null,
    type: apiData.type || 'Individual',
    productName: productName,           // ✅ ADD: Store product name
    planName: planName,                 // ✅ ADD: Store plan name
    source: productName ? `Product Page - ${productName}` : 'Contact Form',
    message: cleanMessage || 'No message provided',
    submittedDate: apiData.createdDate || new Date().toISOString(),
    lastUpdated: apiData.modifiedDate || apiData.createdDate || new Date().toISOString(),
    status: backendStatus,
    priority: backendPriority,
    starred: false,
    assignedTo: null,
    notes: parsedNotes,
    emailHistory: apiData.emailHistory ? JSON.parse(apiData.emailHistory) : [],
    isRepeat: false,
    lastContactDate: null,
    history: [
      {
        action: 'Request submitted',
        user: 'System',
        timestamp: apiData.createdDate || new Date().toISOString()
      }
    ]
  };
};

const AdminDemoRequest = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    status: 'All',
    priority: 'All',
    source: 'All',
    dateFrom: '',
    dateTo: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDrawer, setShowDrawer] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'submittedDate', direction: 'desc' });
  const [exportingPDF, setExportingPDF] = useState(false);
  const [editedRequest, setEditedRequest] = useState(null);
  const [toast, setToast] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null);
  const navigate = useNavigate();

  // Toast helper
  const showToast = (message, type = 'info') => {
    setToast({ message, type });
  };

  // Fetch demo requests
  const fetchDemoRequests = async () => {
    setLoading(true);
    setFetchError(null);

    try {
      const response = await axios.get(`${API_BASE_URL}/demo-request`);

      let apiData = [];
      if (Array.isArray(response.data)) {
        apiData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        apiData = response.data.data;
      } else if (response.data?.demoRequests && Array.isArray(response.data.demoRequests)) {
        apiData = response.data.demoRequests;
      }

      const transformedRequests = apiData.map(transformApiDataToRequest);

      const emailMap = new Map();
      const phoneMap = new Map();

      transformedRequests.forEach(req => {
        if (req.email) {
          if (emailMap.has(req.email)) {
            req.isRepeat = true;
          } else {
            emailMap.set(req.email, true);
          }
        }
        if (req.phone) {
          if (phoneMap.has(req.phone)) {
            req.isRepeat = true;
          } else {
            phoneMap.set(req.phone, true);
          }
        }
      });

      setRequests(transformedRequests);
      showToast('Demo requests loaded successfully', 'success');
    } catch (error) {
      console.error('Error fetching demo requests:', error);
      if (error.response?.status === 405) {
        setFetchError('Backend GET endpoint not available. Please add GET /api/seo-user/demo-request endpoint.');
      } else {
        setFetchError('Failed to load demo requests. Please try again.');
      }
      setRequests([]);
      showToast('Failed to load demo requests', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDemoRequests();
  }, []);

  // Calculate metrics
  const metrics = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    const newToday = requests.filter(r => new Date(r.submittedDate) >= today).length;
    const newThisWeek = requests.filter(r => new Date(r.submittedDate) >= weekAgo).length;
    const pending = requests.filter(r => ['New', 'Contacted', 'Demo Scheduled'].includes(r.status)).length;
    const completed = requests.filter(r => r.status === 'Completed').length;
    const conversionRate = requests.length > 0 ? ((completed / requests.length) * 100).toFixed(1) : 0;

    return {
      total: requests.length,
      newToday,
      newThisWeek,
      pending,
      completed,
      conversionRate
    };
  }, [requests]);

  // Filtered requests
  const filteredRequests = useMemo(() => {
    return requests.filter(request => {
      const matchesSearch =
        request.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        request.phone.includes(searchTerm);

      const matchesStatus = filters.status === 'All' || request.status === filters.status;
      const matchesPriority = filters.priority === 'All' || request.priority === filters.priority;
      const matchesSource = filters.source === 'All' || request.source === filters.source;

      let matchesDate = true;
      if (filters.dateFrom) {
        matchesDate = matchesDate && new Date(request.submittedDate) >= new Date(filters.dateFrom);
      }
      if (filters.dateTo) {
        matchesDate = matchesDate && new Date(request.submittedDate) <= new Date(filters.dateTo);
      }

      return matchesSearch && matchesStatus && matchesPriority && matchesSource && matchesDate;
    });
  }, [requests, searchTerm, filters]);

  // Sorted requests
  const sortedRequests = useMemo(() => {
    const sorted = [...filteredRequests];
    if (sortConfig.key) {
      sorted.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'submittedDate') {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        }

        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sorted;
  }, [filteredRequests, sortConfig]);

  // Paginated requests
  const paginatedRequests = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return sortedRequests.slice(start, start + itemsPerPage);
  }, [sortedRequests, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(sortedRequests.length / itemsPerPage);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleStatusChange = async (id, newStatus) => {
    if (showDrawer && editedRequest?.id === id) {
      setEditedRequest(prev => ({
        ...prev,
        status: newStatus,
        history: [...prev.history, {
          action: `Status changed to ${newStatus}`,
          user: 'Admin',
          timestamp: new Date().toISOString()
        }]
      }));
      setSelectedRequest(prev => ({ ...prev, status: newStatus }));
      return;
    }

    try {
      const numericId = parseInt(id.replace('DR-', ''));
      await demoRequestService.updateDemoRequest(numericId, { status: newStatus });

      setRequests(prev => prev.map(req =>
        req.id === id
          ? {
            ...req,
            status: newStatus,
            history: [...req.history, {
              action: `Status changed to ${newStatus}`,
              user: 'Admin',
              timestamp: new Date().toISOString()
            }]
          }
          : req
      ));

      showToast('Status updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update status:', error);
      showToast('Failed to update status', 'error');
    }
  };

  const handlePriorityChange = async (id, newPriority) => {
    if (showDrawer && editedRequest?.id === id) {
      setEditedRequest(prev => ({
        ...prev,
        priority: newPriority,
        history: [...prev.history, {
          action: `Priority changed to ${newPriority}`,
          user: 'Admin',
          timestamp: new Date().toISOString()
        }]
      }));
      setSelectedRequest(prev => ({ ...prev, priority: newPriority }));
      return;
    }

    try {
      const numericId = parseInt(id.replace('DR-', ''));
      await demoRequestService.updateDemoRequest(numericId, { priority: newPriority });

      setRequests(prev => prev.map(req =>
        req.id === id
          ? {
            ...req,
            priority: newPriority,
            history: [...req.history, {
              action: `Priority changed to ${newPriority}`,
              user: 'Admin',
              timestamp: new Date().toISOString()
            }]
          }
          : req
      ));

      showToast('Priority updated successfully', 'success');
    } catch (error) {
      console.error('Failed to update priority:', error);
      showToast('Failed to update priority', 'error');
    }
  };

  const toggleStar = (id) => {
    setRequests(prev => prev.map(req =>
      req.id === id ? { ...req, starred: !req.starred } : req
    ));
  };

  const openDrawer = (request) => {
    setSelectedRequest(request);
    setEditedRequest({ ...request });
    setShowDrawer(true);
  };

  const closeDrawer = () => {
    setShowDrawer(false);
    setSelectedRequest(null);
    setEditedRequest(null);
    setNewNote('');
  };

  const addNote = () => {
    if (!newNote.trim()) return;

    const newNoteObj = {
      text: newNote,
      user: 'Admin',
      timestamp: new Date().toISOString()
    };

    const updatedNotes = [...(editedRequest.notes || []), newNoteObj];

    setEditedRequest(prev => ({
      ...prev,
      notes: updatedNotes,
      history: [...prev.history, {
        action: 'Note added',
        user: 'Admin',
        timestamp: new Date().toISOString()
      }]
    }));

    setSelectedRequest(prev => ({
      ...prev,
      notes: updatedNotes,
      history: [...prev.history, {
        action: 'Note added',
        user: 'Admin',
        timestamp: new Date().toISOString()
      }]
    }));

    setNewNote('');
    showToast('Note added (save to persist)', 'info');
  };

  const handleSaveAndClose = async () => {
    if (!editedRequest) return;

    try {
      const numericId = parseInt(editedRequest.id.replace('DR-', ''));

      const allNotes = editedRequest.notes && editedRequest.notes.length > 0
        ? editedRequest.notes.map(note =>
          `[${new Date(note.timestamp).toLocaleString()}] ${note.user}: ${note.text}`
        ).join('\n\n')
        : '';

      const updateData = {
        status: editedRequest.status,
        priority: editedRequest.priority,
        comment: allNotes
      };

      await demoRequestService.updateDemoRequest(numericId, updateData);

      setRequests(prev => prev.map(req =>
        req.id === editedRequest.id ? editedRequest : req
      ));

      closeDrawer();
      showToast('Changes saved successfully', 'success');
    } catch (error) {
      console.error('Failed to save changes:', error);
      showToast('Failed to save changes', 'error');
    }
  };

  const applyMetricFilter = (filterType) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);

    setFilters(prev => ({ ...prev, status: 'All' }));
    setCurrentPage(1);

    switch (filterType) {
      case 'newToday':
        setFilters(prev => ({ ...prev, dateFrom: today.toISOString().split('T')[0], dateTo: '' }));
        break;
      case 'newWeek':
        setFilters(prev => ({ ...prev, dateFrom: weekAgo.toISOString().split('T')[0], dateTo: '' }));
        break;
      case 'pending':
        setFilters(prev => ({ ...prev, status: 'New' }));
        break;
      case 'completed':
        setFilters(prev => ({ ...prev, status: 'Completed' }));
        break;
      default:
        setFilters({ status: 'All', priority: 'All', source: 'All', dateFrom: '', dateTo: '' });
    }
  };

  const exportToPDF = () => {
    setExportingPDF(true);

    try {
      const doc = new jsPDF({
        orientation: 'landscape',
        unit: 'mm',
        format: 'a4'
      });

      doc.setFontSize(18);
      doc.setTextColor(40);
      doc.text('Demo Request Report', 14, 15);

      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 22);

      doc.setFontSize(11);
      doc.setTextColor(60);
      doc.text(
        `Total: ${metrics.total} | Pending: ${metrics.pending} | Completed: ${metrics.completed} | Rate: ${metrics.conversionRate}%`,
        14,
        28
      );

      const tableData = sortedRequests.map(req => [
        req.id || '',
        req.name || '',
        req.email || '',
        req.phone || '',
        req.type || '',
        (req.source || '').length > 20 ? req.source.substring(0, 20) + '...' : req.source,
        req.submittedDate ? new Date(req.submittedDate).toLocaleDateString() : '',
        req.status || '',
        req.priority || ''
      ]);

      autoTable(doc, {
        startY: 35,
        head: [['ID', 'Name', 'Email', 'Phone', 'Type', 'Source', 'Date', 'Status', 'Priority']],
        body: tableData,
        theme: 'grid',
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [102, 126, 234], textColor: 255, fontStyle: 'bold', halign: 'center' },
        columnStyles: {
          0: { cellWidth: 22 },
          1: { cellWidth: 30 },
          2: { cellWidth: 45 },
          3: { cellWidth: 28 },
          4: { cellWidth: 22 },
          5: { cellWidth: 35 },
          6: { cellWidth: 25 },
          7: { cellWidth: 25 },
          8: { cellWidth: 20 }
        },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        margin: { left: 14, right: 14 }
      });

      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150);
        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.getWidth() / 2,
          doc.internal.pageSize.getHeight() - 10,
          { align: 'center' }
        );
      }

      const fileName = `demo_requests_${new Date().toISOString().split('T')[0]}.pdf`;
      doc.save(fileName);

      showToast('PDF exported successfully', 'success');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showToast('Failed to generate PDF', 'error');
    } finally {
      setExportingPDF(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) + ' ' +
      date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  const getStatusColor = (status) => {
    const colors = {
      'New': 'adr-status-new',
      'Contacted': 'adr-status-contacted',
      'Demo Scheduled': 'adr-status-scheduled',
      'Completed': 'adr-status-completed',
      'Rejected': 'adr-status-rejected'
    };
    return colors[status] || 'adr-status-new';
  };

  const getPriorityColor = (priority) => {
    const colors = {
      'Low': 'adr-priority-low',
      'Medium': 'adr-priority-medium',
      'High': 'adr-priority-high'
    };
    return colors[priority] || 'adr-priority-low';
  };

  return (
    <AdminLayout>
      <div className="adr-page">
        {/* Toast Notifications */}
        {toast && (
          <Toast
            message={toast.message}
            type={toast.type}
            onClose={() => setToast(null)}
          />
        )}

        {/* Confirmation Modal */}
        {confirmModal && (
          <ConfirmModal
            title={confirmModal.title}
            message={confirmModal.message}
            onConfirm={confirmModal.onConfirm}
            onCancel={() => setConfirmModal(null)}
          />
        )}

        <div className="adr-container">
          {/* Header */}
          <header className="adr-header">
            <div className="adr-header-content">
              <div className="adr-header-title">
                <h1>Demo Request Management</h1>
                <p>Manage and track all demo requests from your platform</p>
              </div>
              <div className="adr-header-actions">
                <button
                  className="adr-btn adr-btn-secondary"
                  onClick={fetchDemoRequests}
                  disabled={loading}
                >
                  <RefreshCw size={18} className={loading ? 'adr-spin' : ''} />
                  {loading ? 'Refreshing...' : 'Refresh'}
                </button>
                <button
                  className="adr-btn adr-btn-primary"
                  onClick={exportToPDF}
                  disabled={exportingPDF || sortedRequests.length === 0}
                >
                  <Download size={18} />
                  {exportingPDF ? 'Exporting...' : 'Export PDF'}
                </button>
                <button
                  className="adr-btn adr-btn-accent"
                  onClick={() => navigate("/admin-products")}
                >
                  <FileText size={18} />
                  Products
                </button>
              </div>
            </div>
          </header>

          {/* Error Alert */}
          {fetchError && (
            <div className="adr-alert adr-alert-error">
              <AlertCircle size={20} />
              <span>{fetchError}</span>
            </div>
          )}

          {/* Loading State */}
          {loading && requests.length === 0 ? (
            <TableSkeleton />
          ) : (
            <>
              {/* Metrics Dashboard */}
              <div className="adr-metrics-grid">
                <div className="adr-metric-card" onClick={() => applyMetricFilter('total')}>
                  <div className="adr-metric-icon adr-metric-total">
                    <TrendingUp size={28} />
                  </div>
                  <div className="adr-metric-content">
                    <div className="adr-metric-value">{metrics.total}</div>
                    <div className="adr-metric-label">Total Requests</div>
                  </div>
                </div>

                <div className="adr-metric-card" onClick={() => applyMetricFilter('newToday')}>
                  <div className="adr-metric-icon adr-metric-new">
                    <AlertCircle size={28} />
                  </div>
                  <div className="adr-metric-content">
                    <div className="adr-metric-value">{metrics.newToday}</div>
                    <div className="adr-metric-label">New Today</div>
                    <div className="adr-metric-subtitle">{metrics.newThisWeek} this week</div>
                  </div>
                </div>

                <div className="adr-metric-card" onClick={() => applyMetricFilter('pending')}>
                  <div className="adr-metric-icon adr-metric-pending">
                    <Clock size={28} />
                  </div>
                  <div className="adr-metric-content">
                    <div className="adr-metric-value">{metrics.pending}</div>
                    <div className="adr-metric-label">Pending</div>
                  </div>
                </div>

                <div className="adr-metric-card" onClick={() => applyMetricFilter('completed')}>
                  <div className="adr-metric-icon adr-metric-completed">
                    <CheckCircle size={28} />
                  </div>
                  <div className="adr-metric-content">
                    <div className="adr-metric-value">{metrics.completed}</div>
                    <div className="adr-metric-label">Completed</div>
                  </div>
                </div>

                <div className="adr-metric-card">
                  <div className="adr-metric-icon adr-metric-conversion">
                    <TrendingUp size={28} />
                  </div>
                  <div className="adr-metric-content">
                    <div className="adr-metric-value">{metrics.conversionRate}%</div>
                    <div className="adr-metric-label">Conversion</div>
                  </div>
                </div>
              </div>

              {/* Filters Section */}
              <div className="adr-filters-section">
                <div className="adr-search-box">
                  <Search size={20} />
                  <input
                    type="text"
                    placeholder="Search by name, email, or phone..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>

                <div className="adr-filter-group">
                  <select value={filters.status} onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}>
                    <option value="All">All Status</option>
                    <option value="New">New</option>
                    <option value="Contacted">Contacted</option>
                    <option value="Demo Scheduled">Demo Scheduled</option>
                    <option value="Completed">Completed</option>
                    <option value="Rejected">Rejected</option>
                  </select>

                  <select value={filters.priority} onChange={(e) => setFilters(prev => ({ ...prev, priority: e.target.value }))}>
                    <option value="All">All Priority</option>
                    <option value="Low">Low</option>
                    <option value="Medium">Medium</option>
                    <option value="High">High</option>
                  </select>

                  <select value={filters.source} onChange={(e) => setFilters(prev => ({ ...prev, source: e.target.value }))}>
                    <option value="All">All Sources</option>
                    <option value="Homepage">Homepage</option>
                    <option value="Contact Form">Contact Form</option>
                    <option value="Pricing Page">Pricing Page</option>
                  </select>

                  <input
                    type="date"
                    value={filters.dateFrom}
                    onChange={(e) => setFilters(prev => ({ ...prev, dateFrom: e.target.value }))}
                  />

                  <input
                    type="date"
                    value={filters.dateTo}
                    onChange={(e) => setFilters(prev => ({ ...prev, dateTo: e.target.value }))}
                  />

                  {(filters.status !== 'All' || filters.priority !== 'All' || filters.source !== 'All' || filters.dateFrom || filters.dateTo) && (
                    <button
                      className="adr-btn-clear-filters"
                      onClick={() => setFilters({ status: 'All', priority: 'All', source: 'All', dateFrom: '', dateTo: '' })}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>

              {/* Results Info */}
              <div className="adr-results-info">
                Showing {paginatedRequests.length} of {sortedRequests.length} requests
                {sortedRequests.length !== requests.length && ` (filtered from ${requests.length} total)`}
              </div>

              {/* Table */}
              {sortedRequests.length === 0 ? (
                <EmptyState
                  message="No demo requests match your filters. Try adjusting your search criteria."
                  icon={Search}
                />
              ) : (
                <div className="adr-table-container">
                  <table className="adr-table">
                    <thead>
                      <tr>
                        <th></th>
                        <th onClick={() => handleSort('id')}>Request ID</th>
                        <th onClick={() => handleSort('name')}>Name</th>
                        <th>Email</th>
                        <th>Phone</th>
                        <th onClick={() => handleSort('type')}>Type</th>
                        <th>Product</th>
                        <th>Plan</th>
                        <th>Source</th>
                        <th>Message</th>
                        <th onClick={() => handleSort('submittedDate')}>Submitted</th>
                        <th>Status</th>
                        <th>Priority</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedRequests.map(request => (
                        <tr key={request.id} className={request.starred ? 'adr-row-starred' : ''}>
                          <td>
                            <button className="adr-btn-icon" onClick={() => toggleStar(request.id)}>
                              {request.starred ? <Star size={18} fill="currentColor" /> : <StarOff size={18} />}
                            </button>
                          </td>
                          <td>
                            <div className="adr-cell-id">
                              {request.id}
                              {request.isRepeat && <span className="adr-badge-repeat">Repeat</span>}
                            </div>
                          </td>
                          <td>
                            <div className="adr-cell-name">
                              <div className="adr-name-primary">{request.name}</div>
                              {request.company && <div className="adr-name-secondary">{request.company}</div>}
                            </div>
                          </td>
                          <td>{request.email}</td>
                          <td>{request.phone}</td>
                          <td>
                            <span className={`adr-badge-type adr-type-${request.type.toLowerCase()}`}>
                              {request.type}
                            </span>
                          </td>
                          <td>
                            <span className="adr-badge-product">
                              {request.productName || 'N/A'}
                            </span>
                          </td>
                          {/* ✅ ADD: Plan column */}
                          <td>
                            <span className="adr-badge-plan">
                              {request.planName || 'N/A'}
                            </span>
                          </td>
                          <td className="adr-cell-source">{request.source}</td>
                          <td>
                            <div className="adr-cell-message">
                              {request.message.substring(0, 50)}...
                              <button className="adr-btn-read-more" onClick={() => openDrawer(request)}>Read More</button>
                            </div>
                          </td>
                          <td className="adr-cell-date">{formatDate(request.submittedDate)}</td>
                          <td>
                            <select
                              className={`adr-select-status ${getStatusColor(request.status)}`}
                              value={request.status}
                              onChange={(e) => handleStatusChange(request.id, e.target.value)}
                            >
                              <option value="New">New</option>
                              <option value="Contacted">Contacted</option>
                              <option value="Demo Scheduled">Demo Scheduled</option>
                              <option value="Completed">Completed</option>
                              <option value="Rejected">Rejected</option>
                            </select>
                          </td>
                          <td>
                            <select
                              className={`adr-select-priority ${getPriorityColor(request.priority)}`}
                              value={request.priority}
                              onChange={(e) => handlePriorityChange(request.id, e.target.value)}
                            >
                              <option value="Low">Low</option>
                              <option value="Medium">Medium</option>
                              <option value="High">High</option>
                            </select>
                          </td>
                          <td>
                            <div className="adr-action-buttons">
                              <button
                                className="adr-btn-action"
                                onClick={() => openDrawer(request)}
                                title="View & Edit Details"
                              >
                                <Edit size={16} />
                                <span>Edit</span>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="adr-pagination">
                  <button
                    className="adr-btn-page"
                    onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={18} />
                    Previous
                  </button>

                  <div className="adr-page-numbers">
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={pageNum}
                          className={`adr-btn-page-number ${currentPage === pageNum ? 'adr-active' : ''}`}
                          onClick={() => setCurrentPage(pageNum)}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>

                  <button
                    className="adr-btn-page"
                    onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                    <ChevronRight size={18} />
                  </button>
                </div>
              )}

              {/* Drawer */}
              {showDrawer && selectedRequest && (
                <div className="adr-drawer-overlay" onClick={closeDrawer}>
                  <div className="adr-drawer" onClick={(e) => e.stopPropagation()}>
                    <div className="adr-drawer-header">
                      <div>
                        <h2>Request Details</h2>
                        <span className="adr-drawer-id">{selectedRequest.id}</span>
                      </div>
                      <button className="adr-btn-close" onClick={closeDrawer}>
                        <X size={24} />
                      </button>
                    </div>

                    <div className="adr-drawer-content">
                      <section className="adr-drawer-section">
                        <h3>Contact Information</h3>
                        <div className="adr-info-grid">
                          <div className="adr-info-item">
                            <label>Name</label>
                            <p>{selectedRequest.name}</p>
                          </div>
                          <div className="adr-info-item">
                            <label>Email</label>
                            <p>{selectedRequest.email}</p>
                          </div>
                          <div className="adr-info-item">
                            <label>Phone</label>
                            <p>{selectedRequest.phone}</p>
                          </div>
                          {selectedRequest.company && (
                            <div className="adr-info-item">
                              <label>Company</label>
                              <p>{selectedRequest.company}</p>
                            </div>
                          )}
                          <div className="adr-info-item">
                            <label>Request Type</label>
                            <p><span className={`adr-badge-type adr-type-${selectedRequest.type.toLowerCase()}`}>{selectedRequest.type}</span></p>
                          </div>
                          <div className="adr-info-item">
                            <label>Source</label>
                            <p>{selectedRequest.source}</p>
                          </div>
                        </div>
                      </section>

                      < section className="adr-drawer-section">
                        <h3>Enquiry Details</h3>
                        <div className="adr-info-grid">
                          <div className="adr-info-item">
                            <label>Product Name</label>
                            <p>
                              {selectedRequest.productName ? (
                                <span className="adr-badge-product">{selectedRequest.productName}</span>
                              ) : (
                                <span style={{ color: '#999' }}>Not specified</span>
                              )}
                            </p>
                          </div>
                          <div className="adr-info-item">
                            <label>Plan Name</label>
                            <p>
                              {selectedRequest.planName ? (
                                <span className="adr-badge-plan">{selectedRequest.planName}</span>
                              ) : (
                                <span style={{ color: '#999' }}>Not specified</span>
                              )}
                            </p>
                          </div>
                          <div className="adr-info-item">
                            <label>Source</label>
                            <p>{selectedRequest.source}</p>
                          </div>
                        </div>
                      </section>

                      <section className="adr-drawer-section">
                        <h3>Message</h3>
                        <div className="adr-message-box">
                          {selectedRequest.message}
                        </div>
                      </section>

                      <section className="adr-drawer-section">
                        <h3>Status & Priority</h3>
                        <div className="adr-info-grid">
                          <div className="adr-info-item">
                            <label>Current Status</label>
                            <select
                              className={`adr-select-status ${getStatusColor(selectedRequest.status)}`}
                              value={selectedRequest.status}
                              onChange={(e) => handleStatusChange(selectedRequest.id, e.target.value)}
                            >
                              <option value="New">New</option>
                              <option value="Contacted">Contacted</option>
                              <option value="Demo Scheduled">Demo Scheduled</option>
                              <option value="Completed">Completed</option>
                              <option value="Rejected">Rejected</option>
                            </select>
                          </div>
                          <div className="adr-info-item">
                            <label>Priority</label>
                            <select
                              className={`adr-select-priority ${getPriorityColor(selectedRequest.priority)}`}
                              value={selectedRequest.priority}
                              onChange={(e) => handlePriorityChange(selectedRequest.id, e.target.value)}
                            >
                              <option value="Low">Low</option>
                              <option value="Medium">Medium</option>
                              <option value="High">High</option>
                            </select>
                          </div>
                          <div className="adr-info-item">
                            <label>Submitted Date</label>
                            <p>{formatDate(selectedRequest.submittedDate)}</p>
                          </div>
                          <div className="adr-info-item">
                            <label>Last Updated</label>
                            <p>{formatDate(selectedRequest.lastUpdated)}</p>
                          </div>
                        </div>
                      </section>

                      <section className="adr-drawer-section">
                        <h3>Internal Notes</h3>
                        <div className="adr-notes-container">
                          {selectedRequest.notes.length > 0 ? (
                            selectedRequest.notes.map((note, idx) => (
                              <div key={idx} className="adr-note-item">
                                <div className="adr-note-header">
                                  <span className="adr-note-user"><User size={14} /> {note.user}</span>
                                  <span className="adr-note-time">{formatDate(note.timestamp)}</span>
                                </div>
                                <div className="adr-note-text">{note.text}</div>
                              </div>
                            ))
                          ) : (
                            <p className="adr-no-notes">No notes added yet</p>
                          )}

                          <div className="adr-add-note">
                            <textarea
                              placeholder="Add internal note..."
                              value={newNote}
                              onChange={(e) => setNewNote(e.target.value)}
                              rows={3}
                            />
                            <button className="adr-btn-add-note" onClick={addNote}>Add Note</button>
                          </div>
                        </div>
                      </section>

                      <section className="adr-drawer-section">
                        <h3>Activity History</h3>
                        <div className="adr-timeline">
                          {selectedRequest.history.map((item, idx) => (
                            <div key={idx} className="adr-timeline-item">
                              <div className="adr-timeline-dot"></div>
                              <div className="adr-timeline-content">
                                <div className="adr-timeline-action">{item.action}</div>
                                <div className="adr-timeline-meta">
                                  {item.user} • {formatDate(item.timestamp)}
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </section>

                      <section className="adr-drawer-section">
                        <h3>Quick Actions</h3>
                        <div className="adr-quick-actions">
                          <button className="adr-btn-quick-action adr-btn-primary">
                            <Mail size={18} />
                            Send Email
                          </button>

                          <button
                            className="adr-btn-quick-action adr-btn-success"
                            onClick={handleSaveAndClose}
                          >
                            <Save size={18} />
                            Save & Close
                          </button>
                        </div>
                      </section>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <SecondaryFooter />
      </div>
    </AdminLayout>
  );
};

export default AdminDemoRequest